import React, { useMemo } from "react"

import { useForm } from "react-hook-form"

import { COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"
import {
    OneVerifyIntegrationCategory,
    OneVerifyIntegrationSupportRequestType,
} from "@common-shared/types/integrations.types"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { TextAreaElement } from "@front-shared/elements/textArea.element"
import type { IPopupProps } from "@front-shared/types/props.types"

import { RuntimeHelper } from "../../helpers/runtime.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { integrationsSubmitSupportRequest } from "../../store/thunks/integrations.thunks"

export const RequestIntegrationPopupComponent: React.FunctionComponent<
    IPopupProps & {
        integrationCategory: OneVerifyIntegrationCategory
    }
> = props => {
    const dispatch = useAppDispatch()

    const {
        handleSubmit,
        formState: { errors, isValid },
        control,
    } = useForm({
        mode: "onChange",
    })

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const isLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isIntegrationSupportRequestLoading()
    }, [runtimeState.isLoading])

    const integrationCategoryLabel = useMemo<string>(() => {
        return (
            COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.categoryLabelMapping[
                props.integrationCategory
            ] ?? "Integration"
        )
    }, [props.integrationCategory])

    const isEraCategory =
        props.integrationCategory === OneVerifyIntegrationCategory.eraData

    const onSubmit = ({ message }: any) => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            integrationsSubmitSupportRequest({
                practice: selectedPracticeRole.practice,
                payload: {
                    requestType:
                        OneVerifyIntegrationSupportRequestType.newIntegrationRequest,
                    integrationCategory: props.integrationCategory,
                    message: message.trim(),
                },
                onSuccess: props.onDone,
            }),
        )
    }

    return (
        <PopupWrapperElement
            isActive={props.isActive}
            className={props.className}
            onDone={props.onDone}
            closeOnOutsideClick={props.closeOnOutsideClick}
            popupWidthClassName="w-[560px] max-w-[calc(100vw-32px)]"
        >
            <div className="flex items-start justify-between gap-[16px]">
                <PrimaryText typography="h3">
                    Request an Integration
                </PrimaryText>
            </div>

            <PrimaryText className="mt-[16px]" weight="medium">
                {isEraCategory
                    ? "Don't see your ERA data source? Let us know what you need and we'll loop in your CSM."
                    : "Don't see your EHR or system? Let us know what you need and we'll loop in your CSM."}
            </PrimaryText>

            <form
                className="mt-[24px]"
                onSubmit={handleSubmit(onSubmit)}
                noValidate={true}
            >
                <PrimaryText className="mb-[8px]" weight="semibold">
                    Which {integrationCategoryLabel} source would you like to
                    connect?
                </PrimaryText>

                <TextAreaElement
                    name="message"
                    placeholder={
                        isEraCategory
                            ? "Waystar — we need nightly ERA remittance data ingested for denial pattern visibility"
                            : "Epic — we use it across all our locations and need eligibility checks to auto-trigger on appointment"
                    }
                    reactHookControl={control}
                    reactHookErrors={errors}
                    reactHookValidations={{
                        required: FRONT_VALIDATION_CONFIG.required,
                        minLength: FRONT_VALIDATION_CONFIG.minLength,
                        maxLength: FRONT_VALIDATION_CONFIG.maxLengthText,
                    }}
                    disabled={isLoading}
                />

                <div className="mt-[24px] flex justify-end gap-[12px]">
                    <ButtonElement
                        label="Cancel"
                        type="default"
                        htmlType="button"
                        disabled={isLoading}
                        onClick={props.onDone}
                    />

                    <ButtonElement
                        label="Submit request"
                        type="primary"
                        htmlType="submit"
                        isLoading={isLoading}
                        disabled={isLoading || !isValid}
                    />
                </div>
            </form>
        </PopupWrapperElement>
    )
}
